import React from 'react';
import { css } from 'linaria';
import { Layout } from 'fronton-react';
import { CubeIcon } from '@fronton/icons-react';
import {
  HeaderSection,
  HeaderImageStructure,
  OtherSection,
  RootLayout,
  DesignRegistrationPanel,
  BenefitsCard,
} from 'common/components';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { useIsInViewport, useScrollToElement } from 'common/hooks';
import { useRegionIdSelector } from 'modules/model-json';
import { ConsultationSection } from 'remotes/consultation-section';
import { PromoBlock } from 'remotes/promo-block';
import { SpecialOffer } from 'remotes/special-offer';
import { OldWindows } from './components/old-windows';
import {
  BENEFITS_CARD_LIST,
  WindowsBanner,
  WindowsProjectGallerySection,
  WindowsProjects,
  WindowsReviews,
} from './components';
import {
  WINDOWS_CDN_PATH,
  WINDOWS_ELEMENT_BLOCK,
  WINDOWS_LANDING_NAME,
  WINDOWS_PATH,
} from './windows-constants';

const windowsBannerDesktop = css`
  @media (--only-mobile) {
    display: none;
  }
`;

const windowsBannerMobile = css`
  && {
    display: none;

    @media (--only-mobile) {
      display: block;
    }
  }
`;

/**
 * Вью страницы "Окна на заказ"
 */
export const Windows: React.FC = () => {
  const { elRef, onScrollToElement } = useScrollToElement();
  const regionId = useRegionIdSelector();

  const isConsultationFormVisible = useIsInViewport(elRef, { threshold: 0 });

  if (regionId === KAZAKHSTAN) {
    return (
      <RootLayout currentPage={WINDOWS_CDN_PATH}>
        <OldWindows />
      </RootLayout>
    );
  }

  return (
    <RootLayout currentPage={WINDOWS_PATH}>
      <HeaderSection
        bgColor="#503329"
        description={`Реализуем любой запрос \u2014${'\n'}от\u00A0классического остекления${'\n'}до\u00A0нестандартных идей`}
        img={
          {
            ldSrcSet: `${WINDOWS_CDN_PATH}/header/new/header-ld.jpg/header-ld.jpg`,
            dSrcSet: `${WINDOWS_CDN_PATH}/header/new/header-d.jpg/header-d.jpg`,
            tSrcSet: `${WINDOWS_CDN_PATH}/header/new/header-t.jpg/header-t.jpg`,
            mSrcSet: `${WINDOWS_CDN_PATH}/header/new/header-m.jpg/header-m.jpg`,
            alt: 'Окна на заказ',
          } as HeaderImageStructure
        }
        mediaType="image"
        title="Пластиковые Окна на заказ"
      />
      <DesignRegistrationPanel
        isConsultationFormVisible={isConsultationFormVisible}
        structure={{
          panel: {
            content: {
              leftIcon: CubeIcon,
              iconColor: 'accent-primary',
              iconType: 'duotone',
              text: 'Запишитесь на консультацию и\u00A0бесплатный\u00A0замер',
            },
            actionBlock: [
              {
                elementBlock: WINDOWS_ELEMENT_BLOCK,
                onClick: onScrollToElement,
                label: 'Записаться',
                target: '_blank',
                variant: 'accent',
              },
            ],
          },
          mobilePanel: {
            content: {
              icon: CubeIcon,
              iconColor: 'accent-primary',
              iconType: 'duotone',
              text: 'Запишитесь на консультацию и\u00A0бесплатный\u00A0замер',
            },
            mobileButton: {
              elementBlock: WINDOWS_ELEMENT_BLOCK,
              openModal: true,
              label: 'Записаться',
            },
            actionBlock: [
              {
                elementBlock: WINDOWS_ELEMENT_BLOCK,
                onClick: onScrollToElement,
                label: 'Записаться',
                target: '_blank',
                variant: 'accent',
              },
            ],
          },
        }}
      />
      <Layout>
        <BenefitsCard
          cardList={BENEFITS_CARD_LIST}
          elementBlock={WINDOWS_ELEMENT_BLOCK}
          elementName="Окна на заказ"
          renderModalContent={({ modal: ModalContent }, onCloseModal) => (
            ModalContent && <ModalContent elementBlock={WINDOWS_ELEMENT_BLOCK} onBtnClick={() => {
              onScrollToElement();
              onCloseModal();
            }} />
          )}
        />
        <PromoBlock elementBlock={WINDOWS_ELEMENT_BLOCK} />
        <SpecialOffer onModalBtnClick={onScrollToElement} elementBlock={WINDOWS_ELEMENT_BLOCK} />
        <WindowsProjects onConsultationSignupBtnClick={onScrollToElement} />
      </Layout>
      <WindowsReviews onConsultationSignupBtnClick={onScrollToElement} regionId={regionId} />
      <Layout>
        <WindowsBanner className={windowsBannerMobile} />
        <WindowsProjectGallerySection onConsultationSignupBtnClick={onScrollToElement} isShowImageName={false} />
        <WindowsBanner className={windowsBannerDesktop} />
        <ConsultationSection
          consultationType="WINDOW"
          elementBlock={WINDOWS_ELEMENT_BLOCK}
          elementText="Отправьте заявку на бесплатную консультацию"
          isCommentVisible
          sectionRef={(el: HTMLElement | null) => {
            elRef.current = el;
          }}
          sectionText="Мы перезвоним в ближайшее время — проконсультируем и запишем на замер"
          sectionTitle="Отправьте заявку на бесплатную консультацию"
        />
      </Layout>
      <OtherSection
        elementBlock={`${WINDOWS_ELEMENT_BLOCK} / other`}
        landingName={WINDOWS_LANDING_NAME}
        promotionName={`${WINDOWS_ELEMENT_BLOCK}_bottom`}
        title="Поможем с обустройством"
      />
    </RootLayout >
  );
};
